import { Component, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CarrierService } from '../../secure/home/services/carrier.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { CarrierRegisterModel } from '../../../shared/objects/carrier/carrierRegisterModel';
import { FormInvalidatorService } from '../../secure/home/services/form-invalidator.service';
import { NgForm } from '@angular/forms';
import { Constants } from '../../../shared/const/constants';
import { BrandingService } from '../../../core/services/branding.service';
import { GlobalResponsivenessService } from '../../../core/services/global-responsiveness.service';

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  standalone: false,
})
export class RegisterComponent implements OnInit {
  @ViewChild('carrierProfileForm', { static: true }) carrierDetails: NgForm;
  @ViewChild('Username', { static: true }) usernameControl: any;
  @ViewChild('Email', { static: true }) emailControl: any;

  registerModel: CarrierRegisterModel = new CarrierRegisterModel();
  registrationAgreementStatus = false;
  submitted = false;
  errors = false;
  usernameIsUsed: boolean;
  emailIsUsed: boolean;
  displayAsColumns: boolean;
  vatHasInvalidCharacters: boolean = false;

  private successMessage: string;
  private errorMessage: string;
  private successMessageTitle: string;
  private errorMessageTitle: string;
  isMobile: boolean;
  isPortraitTablet: boolean;

  get emailRegex(): string {
    return Constants.EMAIL_REGEX_MAX;
  }

  constructor(
    private carrierService: CarrierService,
    private router: Router,
    private toastService: ToastrService,
    private translateService: TranslateService,
    private formInvalidator: FormInvalidatorService,
    private brandingService: BrandingService,
    private cd: ChangeDetectorRef,
    private globalResponsive: GlobalResponsivenessService,
  ) {
    var attributes = this.globalResponsive.getResolutionAttributes();
    this.displayAsColumns = attributes.isPortraitTablet || attributes.isMobile;
  }

  get termsAndConditions(): string {
    return this.brandingService.getTermsAndConditionsLink();
  }

  ngOnInit(): void {
    this.setupToastrMessages();
    this.globalResponsive.isMobile$.subscribe(isMobile => {
      this.isMobile = isMobile;
      this.modifyFormLayout();
    });
    this.globalResponsive.isPortraitTablet$.subscribe(isPortraitTablet => {
      this.isPortraitTablet = isPortraitTablet;
      this.modifyFormLayout();
    });
  }

  modifyFormLayout() {
    this.displayAsColumns = this.isMobile || this.isPortraitTablet;
  }

  onRegister(): void {
    this.submitted = true;
    this.formInvalidator.invalidate(this.carrierDetails, ['website']);

    const usernameIsValid = this.validateUsername(this.usernameControl.control);
    const emailIsValid = this.validateEmail(this.emailControl.control);

    if (!usernameIsValid || !emailIsValid) {
      this.errors = true;
    }

    this.carrierService
      .isUsernameAlreadyUsed(this.registerModel.username)
      .then(response => {
        this.usernameIsUsed = response;
      })
      .then(() => {
        this.carrierService
          .isUserEmailAlreadyUsed(this.registerModel.email)
          .then(response => {
            this.emailIsUsed = response;
          })
          .then(() => {
            this.validate();
          });
      });
  }

  validate(): void {
    this.validateUsername(this.usernameControl.control);
    this.validateEmail(this.emailControl.control);
    if (
      this.registrationAgreementStatus &&
      this.carrierDetails.form.valid &&
      !this.emailIsUsed &&
      !this.usernameIsUsed
    ) {
      this.errors = false;

      const registrationModel = {
        id: this.registerModel.id,
        company: this.registerModel.company.trim(),
        contactPerson: this.registerModel.contactPerson.trim(),
        email: this.registerModel.email.trim(),
        street: this.registerModel.street.trim(),
        city: this.registerModel.city.trim(),
        country: this.registerModel.country.trim(),
        zipCode: this.registerModel.zipCode.trim(),
        phoneNumber: this.registerModel.phoneNumber.trim(),
        mobilePhoneNumber: this.registerModel.mobilePhoneNumber.trim(),
        faxNumber: this.registerModel.faxNumber.trim(),
        website: this.registerModel.website.trim(),
        vatNumber: this.registerModel.vatNumber.trim(),
        chamberOfCommerce: this.registerModel.chamberOfCommerce.trim(),
        username: this.registerModel.username.trim(),
      };

      this.carrierService.registerUser(registrationModel).subscribe(
        () => {
          this.redirect();
          this.toastService.success(
            this.successMessage,
            this.successMessageTitle,
          );
        },
        () => {
          this.redirect();
          this.toastService.error(this.errorMessage, this.errorMessageTitle);
        },
      );
    } else {
      this.errors = true;
      this.cd.markForCheck();
    }
  }

  redirect(): void {
    this.router.navigate(['/login']);
  }

  // TODO: Extract this validation as it is used in register and user details
  validateUsername(usernameControl: any): boolean {
    const usernameMask = new RegExp(Constants.USERNAME_REGEX_MASK);
    if (
      this.usernameIsUsed ||
      this.registerModel.username === '' ||
      !usernameMask.test(this.registerModel.username)
    ) {
      usernameControl.setErrors({ usernameAlreadyTakenOrInvalid: true });
      usernameControl.markAsTouched();
      return false;
    } else {
      usernameControl.setErrors(null);
      usernameControl.markAsPristine();
      return true;
    }
  }

  validateEmail(emailControl: any): boolean {
    const emailMask = new RegExp(Constants.EMAIL_REGEX_MAX);
    if (
      this.emailIsUsed ||
      this.registerModel.email === '' ||
      !emailMask.test(this.registerModel.email)
    ) {
      emailControl.setErrors({ emailInvalid: true });
      emailControl.markAsTouched();
      return false;
    } else {
      emailControl.setErrors(null);
      emailControl.markAsPristine();
      return true;
    }
  }

  clearUsernameError(): void {
    this.usernameIsUsed = false;
    this.usernameControl.control.setErrors(null);
    this.usernameControl.control.markAsPristine();
  }

  clearEmailError(): void {
    this.emailIsUsed = false;
    this.emailControl.control.setErrors({ emailInvalid: null });
    this.emailControl.control.markAsPristine();
  }

  validateVatNumber(): void {
    const vatPattern = /^[A-Z0-9\s]*$/;
    this.vatHasInvalidCharacters = !vatPattern.test(
      this.registerModel.vatNumber || '',
    );
  }

  onVatPaste(_: ClipboardEvent): void {
    setTimeout(() => {
      this.validateVatNumber();
    }, 0);
  }

  private setupToastrMessages(): void {
    this.translateService
      .get('REGISTRATION.SUCCESS-MESSAGE')
      .subscribe(message => {
        this.successMessage = message;
      });
    this.translateService
      .get('REGISTRATION.ERROR-MESSAGE')
      .subscribe(message => {
        this.errorMessage = message;
      });
    this.translateService
      .get('REGISTRATION.SUCCESS-MESSAGE-TITLE')
      .subscribe(message => {
        this.successMessageTitle = message;
      });
    this.translateService
      .get('REGISTRATION.ERROR-MESSAGE-TITLE')
      .subscribe(message => {
        this.errorMessageTitle = message;
      });
  }
}
